import { Utils } from './Utils';
import { Billing_Data, Cash_Count, Fund, Store, Withdrawal } from '../models/RestModels';

export interface CashCloseMovementTotal {
	total: number;
	currency_id: string;
	equivalencia: number;
}

export interface CashCloseTicketReference {
	type: string;
	total: number;
	currency_id: string;
}

/**
 * Display-ready cash close data.
 *
 * Mirrors the values computed by PrintCashClosing2Component
 * (src/app/pages/print-cash-closing2). The component owns every formula; this
 * builder only formats those same values as ESC/POS XML so the USB receipt
 * prints the same numbers as the browser receipt.
 */
export interface CashCloseTicketData {
	store: Store;
	billing_data: Billing_Data | null;
	user_name: string;
	current_date: Date;
	start: string | null;
	end: string | null;
	note: string;
	total_sales: number;
	total_credit: number;
	total_order_discount: number;
	total_item_discount: number;
	total_income_sales: number;
	total_income_other: number;
	total_expenses: number;
	total_expenses_all: number;
	total_withdrawals: number;
	total_returns: number;
	total_funds: number;
	total_in_caja: number;
	cash_on_hand: number;
	diferencia_caja: number;
	currencies_total: number;
	usd_rate: number;
	funds: Fund[];
	withdrawals: Withdrawal[];
	expense_movements_qtys: Record<string, CashCloseMovementTotal>;
	movements_qtys: Record<string, CashCloseMovementTotal>;
	references: CashCloseTicketReference[];
	mxn_bills: Cash_Count[];
	usd_bills: Cash_Count[];
	mxn_coins: Cash_Count[];
	usd_coins: Cash_Count[];
	show_negative_warning: boolean;
}

export class CashCloseTicketBuilder {

	private data: CashCloseTicketData;

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 50;
	font_row: string = 'b';

	constructor(data: CashCloseTicketData, ticket_a_columns: number = 42, ticket_b_columns: number = 50) {
		this.data = data;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
	}

	private getEncoderMaxColumns(font_name: 'a' | 'b' = 'b'): number {
		return font_name == 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	private formatMoney(amount: number): string {
		return Utils.formatMoney(amount || 0);
	}

	private parseDate(value: Date | string | null): Date | null {
		if (!value) return null;
		if (value instanceof Date) return value;
		const d = new Date(value);
		return isNaN(d.getTime()) ? null : d;
	}

	// Same output as the Angular date pipe 'MMMM d, y' with the es locale
	private formatLongDate(value: Date | string | null): string {
		const d = this.parseDate(value);
		if (!d) return '';
		const month = d.toLocaleDateString('es-MX', { month: 'long' });
		return `${month} ${d.getDate()}, ${d.getFullYear()}`;
	}

	// Same output as the Angular date pipe 'h:mm a' with the es locale
	private formatTime(value: Date | string | null): string {
		const d = this.parseDate(value);
		if (!d) return '';
		const hours = d.getHours();
		const h = hours % 12 === 0 ? 12 : hours % 12;
		const m = d.getMinutes() < 10 ? '0' + d.getMinutes() : '' + d.getMinutes();
		return `${h}:${m} ${hours >= 12 ? 'p. m.' : 'a. m.'}`;
	}

	private formatShortDate(value: any): string {
		return Utils.getDateString(value, true);
	}

	private buildTotalsRow(label: string, value: string, maxCols: number, bold: boolean = false): string {
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 2));

		if (bold) {
			return `<Text font="a" bold="true">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
		}
		return `<Text font="a">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
	}

	private buildSectionTitle(title: string): string {
		return `<Text justification="center" bold="true">${this.escapeXml(title)}</Text>\n<Feed lines="1"></Feed>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		xml += this.buildTotalSales();
		xml += this.buildFunds();
		xml += this.buildExpenses();
		xml += this.buildWithdrawals();
		xml += this.buildCaja();
		xml += this.buildNote();
		xml += this.buildIncomeMethods();
		xml += this.buildNegativeWarning();
		xml += this.buildReferences();
		xml += this.buildCashCounts();
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildHeader(): string {
		let xml = '';
		const store = this.data.store;
		const billing = this.data.billing_data;

		xml += `<Text justification="center" bold="true">Sucursal ${this.escapeXml(store?.name || '')}</Text>\n`;

		if (!billing || billing.id == 0) {
			if (store?.business_name) {
				xml += `<Text justification="center">${this.escapeXml(store.business_name)}</Text>\n`;
			}
			if (store?.rfc) {
				xml += `<Text justification="center">${this.escapeXml(store.rfc)}</Text>\n`;
			}
			const parts = [store?.address, store?.city, store?.state, store?.zipcode]
				.filter(p => !!p)
				.map(p => this.escapeXml(p as string));
			if (parts.length) {
				xml += `<Text justification="center">${parts.join(' ')}</Text>\n`;
			}
		} else {
			const razon = `${billing.razon_social || ''} ${billing.regimen_capital || ''}`.trim();
			if (razon) {
				xml += `<Text justification="center">${this.escapeXml(razon.toUpperCase())}</Text>\n`;
			}
			if (billing.rfc) {
				xml += `<Text justification="center">${this.escapeXml(billing.rfc)}</Text>\n`;
			}
			const parts = [billing.address, billing.city, billing.state]
				.filter(p => !!p)
				.map(p => this.escapeXml(p as string));
			if (parts.length) {
				xml += `<Text justification="center">${parts.join(' ')}</Text>\n`;
			}
			if (billing.zipcode) {
				xml += `<Text justification="center">${this.escapeXml(billing.zipcode)}</Text>\n`;
			}
		}

		if (store?.phone) {
			xml += `<Text justification="center">Tel: ${this.escapeXml(store.phone)}</Text>\n`;
		}

		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('CORTE DE CAJA');

		xml += `<Text font="b" justification="left">Solicitado por: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.data.user_name || '')}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">Fecha: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatLongDate(this.data.current_date))}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">Hora: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatTime(this.data.current_date))}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">De: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatLongDate(this.data.start) + ', ' + this.formatTime(this.data.start))}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">A: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatLongDate(this.data.end) + ', ' + this.formatTime(this.data.end))}</Text><Feed lines="1"></Feed>\n`;
		xml += '<Feed lines="1"></Feed>\n';

		return xml;
	}

	private buildTotalSales(): string {
		const maxCols = this.getEncoderMaxColumns('a');
		return this.buildTotalsRow('Total Ventas', this.formatMoney(this.data.total_sales), maxCols, true);
	}

	private buildFunds(): string {
		if (!this.data.funds?.length) return '';
		const maxCols = this.getEncoderMaxColumns('a');
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('Fondo');
		for (const fund of this.data.funds) {
			const label = this.formatShortDate(fund.created);
			xml += this.buildTotalsRow(label, `${this.formatMoney(fund.amount)} ${fund.currency_id || ''}`.trim(), maxCols);
		}
		return xml;
	}

	private buildExpenses(): string {
		const keys = Object.keys(this.data.expense_movements_qtys || {}).sort();
		if (!keys.length) return '';
		const maxCols = this.getEncoderMaxColumns('a');
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('Gastos');
		for (const key of keys) {
			const item = this.data.expense_movements_qtys[key];
			xml += this.buildTotalsRow(key, `${this.formatMoney(item.total)} ${item.currency_id || ''}`.trim(), maxCols);
		}
		xml += this.buildTotalsRow('Total Gastos', this.formatMoney(this.data.total_expenses_all), maxCols, true);
		return xml;
	}

	private buildWithdrawals(): string {
		if (!this.data.withdrawals?.length) return '';
		const maxCols = this.getEncoderMaxColumns('a');
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('Retiros');
		for (const withdrawal of this.data.withdrawals) {
			const label = `${this.formatShortDate(withdrawal.created)} - ${withdrawal.note || ''}`.trim();
			xml += this.buildTotalsRow(label, `${this.formatMoney(withdrawal.amount)} ${withdrawal.currency_id || ''}`.trim(), maxCols);
		}
		return xml;
	}

	private buildCaja(): string {
		const maxCols = this.getEncoderMaxColumns('a');
		const d = this.data;
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('CAJA');

		xml += this.buildTotalsRow('Total Ventas', this.formatMoney(d.total_sales), maxCols, true);
		xml += this.buildTotalsRow('Credito', this.formatMoney(d.total_credit), maxCols);
		xml += this.buildTotalsRow('Descuentos Ordenes', this.formatMoney(d.total_order_discount), maxCols);
		xml += this.buildTotalsRow('Descuentos Articulos', this.formatMoney(d.total_item_discount), maxCols);
		xml += this.buildTotalsRow('Total Pagos de Ventas', this.formatMoney(d.total_income_sales), maxCols);
		xml += this.buildTotalsRow('Total Otros Pagos', this.formatMoney(d.total_income_other), maxCols);
		xml += this.buildTotalsRow('Gastos', this.formatMoney(d.total_expenses), maxCols);
		xml += this.buildTotalsRow('Retiros', this.formatMoney(d.total_withdrawals), maxCols);
		xml += this.buildTotalsRow('Articulos Cancelados', this.formatMoney(d.total_returns), maxCols);
		xml += this.buildTotalsRow('Total Fondos', this.formatMoney(d.total_funds), maxCols);
		xml += this.buildTotalsRow('Total Ingresos', this.formatMoney(d.total_income_sales + d.total_income_other - d.total_expenses), maxCols);
		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildTotalsRow('Total en caja', this.formatMoney(d.total_in_caja), maxCols, true);
		xml += this.buildTotalsRow('Efectivo En Caja', this.formatMoney(d.cash_on_hand), maxCols, true);

		const diffLabel = d.diferencia_caja > 0 ? 'Diferencia sobra' : 'Diferencia Falta';
		const diffValue = d.diferencia_caja > 0 ? d.diferencia_caja : -d.diferencia_caja;
		xml += this.buildTotalsRow(diffLabel, this.formatMoney(diffValue), maxCols, true);

		return xml;
	}

	private buildNote(): string {
		if (!this.data.note) return '';
		let xml = '<Feed lines="1"></Feed>\n';
		xml += `<Text font="b" bold="true" justification="left">Notas</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">${this.escapeXml(this.data.note)}</Text><Feed lines="1"></Feed>\n`;
		return xml;
	}

	private buildIncomeMethods(): string {
		const keys = Object.keys(this.data.movements_qtys || {}).sort();
		if (!keys.length) return '';
		const maxCols = this.getEncoderMaxColumns('a');
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('Formas de ingreso');
		for (const key of keys) {
			const item = this.data.movements_qtys[key];
			if (item.currency_id == 'USD') {
				xml += `<Text font="b" justification="right">${this.escapeXml(this.formatMoney(item.total))} x ${this.data.usd_rate}</Text><Feed lines="1"></Feed>\n`;
			}
			xml += this.buildTotalsRow(key, this.formatMoney(item.equivalencia), maxCols);
		}
		xml += this.buildTotalsRow('Total', this.formatMoney(this.data.currencies_total), maxCols, true);
		return xml;
	}

	private buildNegativeWarning(): string {
		if (!this.data.show_negative_warning) return '';
		let xml = '<Feed lines="1"></Feed>\n';
		xml += `<Text justification="center">El "Total en caja" es negativo debido a que el monto de las cancelaciones realizadas es mayor que el ingreso en efectivo.</Text>\n`;
		xml += '<Feed lines="1"></Feed>\n';
		return xml;
	}

	private buildReferences(): string {
		if (!this.data.references?.length) return '';
		const maxCols = this.getEncoderMaxColumns('a');
		const legend: Record<string, string> = {
			'CREDIT_CARD': 'Tarjeta Crédito',
			'DEBIT_CARD': 'Tarjeta Débito',
			'CHECK': 'Cheque',
			'TRANSFER': 'Transferencia'
		};
		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('INGRESOS NO FISICOS');
		for (const ref of this.data.references) {
			xml += this.buildTotalsRow(legend[ref.type] || ref.type, this.formatMoney(ref.total), maxCols);
		}
		return xml;
	}

	private buildCashCounts(): string {
		let xml = '';
		xml += this.buildCashCountTable('BILLETES EN MONEDA NACIONAL', this.data.mxn_bills);
		xml += this.buildCashCountTable('BILLETES EN DOLARES', this.data.usd_bills);
		xml += this.buildCashCountTable('MONEDAS EN M.N.', this.data.mxn_coins);
		xml += this.buildCashCountTable('MONEDAS EN DOLARES', this.data.usd_coins);
		return xml;
	}

	private buildCashCountTable(title: string, rows: Cash_Count[]): string {
		if (!rows?.length) return '';
		const maxCols = this.getEncoderMaxColumns(this.font_row as 'a' | 'b');
		const qtyWidth = 6;
		const totalWidth = 14;
		const denomWidth = Math.max(8, maxCols - qtyWidth - totalWidth - 2);

		let columnsXml = '<Columns>\n';
		columnsXml += `<Column width="${denomWidth}" align="left" marginRight="1"></Column>\n`;
		columnsXml += `<Column width="${qtyWidth}" align="right" marginRight="1"></Column>\n`;
		columnsXml += `<Column width="${totalWidth}" align="right"></Column>\n`;
		columnsXml += '</Columns>\n';

		let rowsXml = '<Row>\n';
		rowsXml += '<Cell><Text>Denom.</Text></Cell>\n';
		rowsXml += '<Cell><Text>Cant.</Text></Cell>\n';
		rowsXml += '<Cell><Text>Total</Text></Cell>\n';
		rowsXml += '</Row>\n';

		let total = 0;
		for (const row of rows) {
			const lineTotal = (row.denomination || 0) * (row.quantity || 0);
			total += lineTotal;
			rowsXml += '<Row>\n';
			rowsXml += `<Cell><Text>${this.escapeXml(this.formatMoney(row.denomination || 0))}</Text></Cell>\n`;
			rowsXml += `<Cell><Text>${row.quantity || 0}</Text></Cell>\n`;
			rowsXml += `<Cell><Text>${this.escapeXml(this.formatMoney(lineTotal))}</Text></Cell>\n`;
			rowsXml += '</Row>\n';
		}

		rowsXml += '<Row>\n';
		rowsXml += '<Cell><Text>TOTAL</Text></Cell>\n';
		rowsXml += '<Cell><Text></Text></Cell>\n';
		rowsXml += `<Cell><Text>${this.escapeXml(this.formatMoney(total))}</Text></Cell>\n`;
		rowsXml += '</Row>\n';

		let xml = '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle(title);
		xml += `<Table font="${this.font_row}">\n${columnsXml}<Rows>\n${rowsXml}</Rows>\n</Table>\n`;
		return xml;
	}
}
